(ns portfolio.content
  "All copy + project metadata lives here as data. Translatable values are
  maps `{:en ... :pt ...}`. Plain strings (URLs, language-agnostic labels,
  the EDN code-card tokens) stay as-is.")

(def github-user "AlexsandroRyan")

(def nav-labels
  {:about    {:en "About"    :pt "Sobre"}
   :work     {:en "Work"     :pt "Trabalho"}
   :contact  {:en "Contact"  :pt "Contato"}
   :github   {:en "GitHub ↗" :pt "GitHub ↗"}})

(def hero
  {:eyebrow    {:en "Product Engineer · AI-native"
                :pt "Engenheiro de Produto · IA-nativo"}
   :name-first "Alexsandro"
   :name-last  "Ryan"
   :role       {:en "I ship products end to end and orchestrate a fleet of coding agents in parallel to do it at multi-engineer pace."
                :pt "Construo produtos ponta a ponta e orquestro uma frota de agentes de programação em paralelo para fazer isso no ritmo de um time inteiro."}
   :status     {:en "Currently at Nubank · Open to chat"
                :pt "Atualmente no Nubank · Aberto a conversas"}
   :chips      ["Product Engineering" "AI Agents" "Clojure" "TypeScript" "React Native" "Datomic" "AWS"]
   :code-card  {:title "alexsandro.edn"
                :body  [[:line [[:tok-paren "{"] [:tok-key ":name"] "    " [:tok-str "\"Alexsandro Ryan\""]]]
                        [:line [" " [:tok-key ":role"] "    " [:tok-kw ":product-engineer"]]]
                        [:line [" " [:tok-key ":works"] "   " [:tok-kw ":end-to-end"]]]
                        [:line [" " [:tok-key ":stack"] "   " [:tok-paren "#{"] [:tok-kw ":clojure"] " " [:tok-kw ":typescript"]]]
                        [:line ["            " [:tok-kw ":react-native"]]]
                        [:line ["            " [:tok-kw ":datomic"] " " [:tok-kw ":aws"] [:tok-paren "}"]]]
                        [:line [" " [:tok-key ":agents"] "  " [:tok-kw ":orchestrated"]]]
                        [:line [" " [:tok-key ":ships"] "   " [:tok-kw ":small-and-finished"] [:tok-paren "}"]]]]}})

(def stats
  [{:value "3.5+"
    :label {:en "years\nin prod"
            :pt "anos em\nprodução"}}
   {:value "300K+"
    :label {:en "users on\nToyota DK"
            :pt "usuários na\nToyota DK"}}
   {:value "2.5+"
    :label {:en "yrs in\nClojure"
            :pt "anos com\nClojure"}}
   {:value "100%"
    :label {:en "AI-native\nat Nubank"
            :pt "IA-nativo\nno Nubank"}}])

(def how-i-work
  {:section {:eyebrow-num "01"
             :title {:en "How I work"
                     :pt "Como trabalho"}}
   :cards   [{:heading {:en "End to end, on purpose"
                        :pt "Ponta a ponta, de propósito"}
              :body    {:en "I take a feature from rough idea to live and earning trust with users — frontend, backend, infrastructure, deploy pipelines, the boring middle. The whole thing. I'm at my best when I own a slice end to end and stay accountable for the outcome, not just a layer of it."
                        :pt "Pego uma funcionalidade desde a ideia bruta até estar em produção ganhando a confiança dos usuários — frontend, backend, infraestrutura, pipelines de deploy, o meio chato. A coisa toda. Rendo melhor quando posso ser dono de uma fatia ponta a ponta e prestar contas pelo resultado, não só por uma camada."}}
             {:heading {:en "AI-native, not AI-assisted"
                        :pt "IA-nativo, não IA-assistido"}
              :body    {:en "I orchestrate a fleet of coding agents in parallel — one on the spec, another on the database work, a third chasing failing tests — while I drive design decisions. At Nubank this is the whole workflow, and what one engineer ships is genuinely closer to what a small team used to ship the old way."
                        :pt "Orquestro uma frota de agentes de programação em paralelo — um na spec, outro no trabalho de banco, um terceiro caçando os testes que falham — enquanto dirijo as decisões de design. No Nubank esse é o fluxo inteiro, e o que um engenheiro entrega desse jeito está genuinamente perto do que um time pequeno entregava do jeito antigo."}}]
   :footer  {:en "Background: 3.5+ years shipping production systems — mostly Clojure on the backend, ClojureScript or TypeScript on the client, React Native for mobile. At Vouch.io I helped ship the Toyota Digital Key mobile SDK and backend serving 300K+ active users in Toyota America and Europe, and the IdP integration of a passwordless identity platform now used by enterprises to retire password flows."
             :pt "Background: 3.5+ anos entregando sistemas em produção — principalmente Clojure no backend, ClojureScript ou TypeScript no client, React Native para mobile. Na Vouch.io ajudei a entregar o SDK mobile e o backend do Toyota Digital Key servindo 300K+ usuários ativos na Toyota America e Europa, e a integração de IdP de uma plataforma de identidade sem senha hoje usada por empresas para aposentar fluxos de senha."}})

(def projects-section
  {:eyebrow-num "02"
   :title {:en "Selected work"
           :pt "Trabalhos selecionados"}})

(def projects
  [{:title    "flaglog"
    :tag      {:en "Featured" :pt "Destaque"}
    :desc     {:en "A small feature-flag service with a built-in audit log. Datomic's time-travel queries are the natural answer to 'who flipped this flag at 3:47am last Tuesday' — no audit table required. Ring + Reitit on Clojure, deployable to AWS via Terraform."
               :pt "Um pequeno serviço de feature flags com log de auditoria embutido. As queries de viagem no tempo do Datomic respondem naturalmente \"quem mudou essa flag às 3h47 da terça passada\" — sem precisar de tabela de auditoria. Ring + Reitit em Clojure, deployável na AWS via Terraform."}
    :preview  ["$ curl -X PUT flaglog/flags/checkout.new \\"
               "    -d '{\"value\":\"true\","
               "         \"actor\":\"alex\","
               "         \"reason\":\"go live\"}'"]
    :stack    ["Clojure" "Datomic" "Ring/Reitit" "AWS" "Terraform"]
    :repo     "https://github.com/AlexsandroRyan/flaglog"
    :live     nil}
   {:title    "Toyota Digital Key"
    :tag      {:en "Vouch.io · Production"
               :pt "Vouch.io · Produção"}
    :desc     {:en "The car key, on a phone. Mobile SDK and backend services for Toyota's Digital Key product, live on 300K+ vehicles across Toyota North America and Toyota Europe. Contributed across React Native and Clojure on the device-to-vehicle handshake, performance investigation on the event-query path, and production support for an enterprise client."
               :pt "A chave do carro, no celular. SDK mobile e serviços de backend para o Toyota Digital Key, em produção em 300K+ veículos na Toyota Norte-América e Europa. Contribuí em React Native e Clojure no handshake dispositivo-veículo, investigação de performance no caminho de event-query, e suporte em produção para cliente enterprise."}
    :preview  ["# Toyota Digital Key — unlock flow"
               "phone   → BLE handshake (vehicle)"
               "vehicle → validate(token, ts)"
               "backend → audit(event, user)"
               "vehicle → unlock ✓"]
    :stack    ["React Native" "Clojure" "ClojureScript" "AWS" "PostgreSQL" "BLE"]
    :repo     nil
    :live     nil}
   {:title    "Vouch Identity"
    :tag      {:en "Vouch.io · Production"
               :pt "Vouch.io · Produção"}
    :desc     {:en "Replacing passwords. A passwordless authentication platform built on encrypted device keys and QR-code onboarding, with IdP integration that lets enterprises retire password flows for their internal apps. Owned IdP integration and backend API routes in Clojure; mobile in React Native + ClojureScript with secure cryptographic key storage."
               :pt "Aposentando senhas. Plataforma de autenticação sem senha, construída em chaves criptográficas no dispositivo e onboarding por QR-code, com integração IdP que permite que empresas aposentem fluxos de senha em apps internos. Fui dono da integração IdP e das rotas de API backend em Clojure; mobile em React Native + ClojureScript com armazenamento seguro de chaves criptográficas."}
    :preview  ["# passwordless login via QR"
               "user  → scans QR on app login"
               "app   → /idp/challenge {qr-id}"
               "phone → /idp/respond {device-key}"
               "app   → session ✓  (no password)"]
    :stack    ["Clojure" "ClojureScript" "React Native" "Datomic" "PostgreSQL" "AWS"]
    :repo     nil
    :live     nil}])

(def contact-section
  {:eyebrow-num "03"
   :title  {:en "Get in touch"     :pt "Entre em contato"}
   :opener {:en "Open to Product Engineer roles, especially at startups doubling down on AI-native workflows. The fastest path is email or LinkedIn."
            :pt "Aberto a vagas de Engenheiro de Produto, especialmente em startups apostando em fluxos AI-native. O caminho mais rápido é email ou LinkedIn."}})

(def contact
  [{:label "Email"    :href "mailto:alexsandrocosta855@gmail.com" :text "alexsandrocosta855@gmail.com"}
   {:label "GitHub"   :href (str "https://github.com/" github-user) :text (str "@" github-user)}
   {:label "LinkedIn" :href "https://www.linkedin.com/in/alexsandroryan/" :text "linkedin.com/in/alexsandroryan"}])

(def footer
  {:owner-name "Alexsandro Ryan"
   :prompt     {:en "echo \"thanks for visiting — let's ship something\""
                :pt "echo \"obrigado pela visita — vamos construir algo\""}
   :source-link {:en "source ↗" :pt "código ↗"}
   :repo-url   (str "https://github.com/" github-user "/portfolio")})
